
import AccountHeader from './shared/AccountHeader.vue';
import AccountPagination from './shared/AccountPagination.vue';
import Appointment from './appointments/Appointment.vue';
import AppointmentService from '../services/AppointmentService';
import FullScreenLoader from 'JS/components/FullScreenLoader.vue';
import TransitionFade from 'JS/components/transitions/TransitionFade.vue';
import UiButton from 'JS/components/UiButton.vue';
import { computed, defineComponent, onMounted, ref } from 'vue';
import { getAppointmentEndDate, getAppointmentStartDate } from 'JS/automotive/auto-service-scheduler/utils/helpers';
import { IAccountAppointmentSearchResult } from '../types/appointment';
import { IWorkOrderConfirmationResource } from 'JS/types/Automotive';
import { useEventListener } from 'JS/composables/event-listener';

type WorkOrder = IWorkOrderConfirmationResource & { displayType: 'past' | 'upcoming' };

const numPerPage = 6;

export default defineComponent({
	name: 'AppointmentApp',
	components: {
		Appointment,
		AccountHeader,
		AccountPagination,
		FullScreenLoader,
		TransitionFade,
		UiButton
	},
	setup() {
		const loading = ref(false);
		const _appointmentService = new AppointmentService();


		const allWorkOrders = ref<WorkOrder[]>([]);
		// NOTE: the backend returns a value for this, but is not reliable in dev.
		//       since we return all valid work orders from the backend, simpler
		//       to derive this from that array
		const totalResults = computed(() => allWorkOrders.value.length);
		const totalPages = computed(() => Math.ceil(totalResults.value / numPerPage));

		const page = ref(1);
		const currentPageOfWorkOrders = computed(() => allWorkOrders.value.slice((page.value - 1) * numPerPage, page.value * numPerPage));

		const orderSections = computed(() => currentPageOfWorkOrders.value.reduce((sections, wo) => {
			const section = wo.displayType === 'upcoming' ? sections.upcoming : sections.past;

			section.push(wo);

			return sections;
		}, { upcoming: [] as IWorkOrderConfirmationResource[], past: [] as IWorkOrderConfirmationResource[] }));

		onMounted(() => {
			const url = new URL(window.location.href);
			if (url.pathname.endsWith('appointments/')) {
				getWorkOrders();
			}
		});

		useEventListener(window, 'accounttabchange', e => {
			const url = new URL(window.location.href);

			if (e.detail.startsWith('appointments') && url.pathname.endsWith(e.detail) && !loading.value) {
				allWorkOrders.value = [];
				page.value = 1;
				getWorkOrders();
			}
		});

		async function getWorkOrders() {
			loading.value = true;

			const workOrderResults: IAccountAppointmentSearchResult = await _appointmentService.searchAppointments();

			allWorkOrders.value = workOrderResults.workOrders.map(wo => ({
				...wo,
				displayType: getAppointmentEndDate(wo.appointmentInfo) > new Date()
					? 'upcoming'
					: 'past',
			}) as WorkOrder).sort((a, b) => {
				if (a.displayType === b.displayType) {
					const aDate = getAppointmentStartDate(a.appointmentInfo);
					const bDate = getAppointmentStartDate(b.appointmentInfo);
					if (a.displayType === 'upcoming') {
						// Upcoming dates are sorted by end date asc. sooner first
						return aDate.getTime() - bDate.getTime();
					} else {
						// Past dates are sorted by end date desc. most recently in the past first
						return bDate.getTime() - aDate.getTime();
					}
				}

				// upcoming dates are always shown before past dates
				if (a.displayType === 'past' && b.displayType === 'upcoming') {
					return 1;
				}

				// a.displayType === 'upcoming' && b.displayType === 'past'
				return -1;
			});

			loading.value = false;
		}

		return {
			loading,
			page,
			totalPages,
			totalResults,
			orderSections,
		};
	}
});
